import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Images, ImagePlus, Loader2, Search, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { GET_MY_USER_MEDIA, GET_MY_USER_MEDIA_FOLDERS } from '@/queries/queries';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 40;

export interface MyMediaPickerItem {
  id: string;
  url: string;
  kind: 'image';
  name?: string | null;
  width?: number | null;
  height?: number | null;
  folder_id?: string | null;
  createdAt?: string | null;
}

interface MyMediaFolder {
  id: string;
  name: string;
  media_count: number;
}

export interface MyMediaPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: MyMediaPickerItem) => void;
  onUploadInstead?: () => void;
  onBrowseStock?: () => void;
}

export const MyMediaPickerDialog = ({
  open,
  onOpenChange,
  onSelect,
  onUploadInstead,
  onBrowseStock,
}: MyMediaPickerDialogProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [folderId, setFolderId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const variables = useMemo(() => {
    const next: {
      kind: string;
      folderId?: string;
      search?: string;
      limit: number;
      offset: number;
    } = { kind: 'image', limit: PAGE_SIZE, offset: 0 };
    if (folderId) next.folderId = folderId;
    if (debouncedSearch) next.search = debouncedSearch;
    return next;
  }, [folderId, debouncedSearch]);

  const { data: foldersData } = useQuery(GET_MY_USER_MEDIA_FOLDERS, {
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });
  const { data, loading, error, refetch, fetchMore } = useQuery(GET_MY_USER_MEDIA, {
    variables,
    skip: !open,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const folders: MyMediaFolder[] = foldersData?.myUserMediaFolders ?? [];
  const items: MyMediaPickerItem[] = data?.myUserMedia ?? [];

  useEffect(() => {
    setHasMore(true);
  }, [folderId, debouncedSearch]);

  useEffect(() => {
    if (!loading && items.length < PAGE_SIZE) setHasMore(false);
  }, [items.length, loading]);

  const loadMore = async () => {
    if (loadingMoreRef.current || loading || error || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: { ...variables, offset: items.length },
      });
      if ((result.data?.myUserMedia?.length ?? 0) < PAGE_SIZE) setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const pick = (item: MyMediaPickerItem) => {
    onSelect(item);
    onOpenChange(false);
  };

  const chipClass = (active: boolean) => cn(
    'shrink-0 rounded-full border px-2.5 py-1 text-xs transition-colors',
    active
      ? 'border-primary bg-primary/10 text-primary'
      : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted/60 hover:text-foreground',
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col overflow-hidden p-0">
        <DialogHeader className="border-b border-border/70 px-5 pb-4 pt-5">
          <div className="flex items-start gap-3 pr-8">
            <div className="mt-0.5 rounded-lg bg-primary/10 p-2 text-primary">
              <Images className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="space-y-1">
              <DialogTitle>{t('myMedia.picker.title')}</DialogTitle>
              <DialogDescription>{t('myMedia.picker.description')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 pb-5">
          <div className="flex flex-col gap-2 pt-4 sm:flex-row">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                autoFocus
                data-testid="my-media-picker-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('myMedia.picker.searchPlaceholder')}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              {onBrowseStock && (
                <Button type="button" variant="outline" onClick={onBrowseStock}>
                  <ImagePlus className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  {t('myMedia.picker.browseStock')}
                </Button>
              )}
              {onUploadInstead && (
                <Button type="button" variant="outline" onClick={onUploadInstead}>
                  <Upload className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  {t('myMedia.picker.uploadInstead')}
                </Button>
              )}
            </div>
          </div>

          <div className="flex gap-1.5 overflow-x-auto pb-1" aria-label={t('myMedia.picker.folderFilter')}>
            <button
              type="button"
              data-testid="my-media-picker-folder-all"
              aria-pressed={folderId === null}
              className={chipClass(folderId === null)}
              onClick={() => setFolderId(null)}
            >
              {t('myMedia.folders.all')}
            </button>
            <button
              type="button"
              data-testid="my-media-picker-folder-root"
              aria-pressed={folderId === 'root'}
              className={chipClass(folderId === 'root')}
              onClick={() => setFolderId('root')}
            >
              {t('myMedia.folders.root')}
            </button>
            {folders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                data-testid={`my-media-picker-folder-${folder.id}`}
                aria-pressed={folderId === folder.id}
                className={chipClass(folderId === folder.id)}
                onClick={() => setFolderId(folder.id)}
              >
                {folder.name}
              </button>
            ))}
          </div>

          <div className="min-h-[260px] flex-1 overflow-y-auto rounded-lg border border-border/70 bg-muted/10 p-2">
            {error && items.length === 0 ? (
              <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 text-center">
                <p className="text-sm text-muted-foreground">{t('myMedia.errorLoading')}</p>
                <Button type="button" variant="secondary" size="sm" onClick={() => refetch()}>
                  {t('myMedia.retry')}
                </Button>
              </div>
            ) : loading && items.length === 0 ? (
              <div className="flex min-h-[240px] items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('myMedia.picker.loading')}
              </div>
            ) : items.length === 0 ? (
              <div className="flex min-h-[240px] flex-col items-center justify-center gap-2 px-6 text-center">
                <Images className="h-8 w-8 text-muted-foreground/50" aria-hidden="true" />
                <p className="text-sm font-medium">{t('myMedia.picker.emptyTitle')}</p>
                <p className="max-w-sm text-xs text-muted-foreground">
                  {t('myMedia.picker.emptyDescription')}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  {items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      data-testid={`my-media-picker-item-${item.id}`}
                      title={item.name ?? t('myMedia.item.untitled')}
                      onClick={() => pick(item)}
                      className="group overflow-hidden rounded-md border border-border bg-card text-left transition hover:-translate-y-0.5 hover:border-primary/70 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <div className="aspect-square overflow-hidden bg-black/20">
                        <img
                          src={item.url}
                          alt={item.name ?? ''}
                          loading="lazy"
                          className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.03]"
                        />
                      </div>
                      <div className="px-2 py-1.5">
                        <p className="truncate text-[11px] font-medium">
                          {item.name || t('myMedia.item.untitled')}
                        </p>
                        {item.width && item.height ? (
                          <p className="text-[10px] text-muted-foreground">{item.width} × {item.height}</p>
                        ) : null}
                      </div>
                    </button>
                  ))}
                </div>
                {hasMore && (
                  <div className="flex justify-center pt-3">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={loadingMore}
                      onClick={loadMore}
                    >
                      {loadingMore ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                      {t('myMedia.loadMore')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MyMediaPickerDialog;
